import { useEffect, useRef, useCallback } from "react";
import Hls from "hls.js";

interface CloudflareVideoPlayerProps {
  hlsUrl: string;
  playbackSpeed?: number;
  muted?: boolean;
  onTimeUpdate?: (time: number) => void;
  onDurationChange?: (duration: number) => void;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onError?: (message: string) => void;
  onVideoRef?: (el: HTMLVideoElement | null) => void;
  onProgress?: (videoEl: HTMLVideoElement) => void;
  onWaiting?: () => void;
  onPlaying?: () => void;
  onRetrying?: (retrying: boolean) => void;
  className?: string;
}

export function CloudflareVideoPlayer({
  hlsUrl,
  playbackSpeed = 1,
  muted = false,
  onTimeUpdate,
  onDurationChange,
  onPlay,
  onPause,
  onEnded,
  onError,
  onVideoRef,
  onProgress,
  onWaiting,
  onPlaying,
  onRetrying,
  className,
}: CloudflareVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const onVideoRefLatest = useRef(onVideoRef);
  onVideoRefLatest.current = onVideoRef;
  const onErrorLatest = useRef(onError);
  onErrorLatest.current = onError;
  const onRetryingLatest = useRef(onRetrying);
  onRetryingLatest.current = onRetrying;

  const setVideoRef = useCallback((el: HTMLVideoElement | null) => {
    videoRef.current = el;
    onVideoRefLatest.current?.(el);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hlsUrl) return;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    let networkRetryTimer: ReturnType<typeof setTimeout> | null = null;

    if (Hls.isSupported()) {
      let mediaErrorRecoveries = 0;
      let networkErrorRecoveries = 0;
      const MAX_NETWORK_RETRIES = 8;
      const NETWORK_RETRY_BASE_MS = 1000;
      const NETWORK_RETRY_MAX_MS = 15000;

      const hls = new Hls();
      hlsRef.current = hls;

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        onRetryingLatest.current?.(false);
        if (playbackSpeed !== 1) {
          video.playbackRate = playbackSpeed;
        }
      });

      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal) return;

        if (data.details === "manifestIncompatibleCodecsError") {
          hls.destroy();
          hlsRef.current = null;
          if (video.canPlayType("application/vnd.apple.mpegurl")) {
            video.src = hlsUrl;
            video.load();
          } else {
            onErrorLatest.current?.("Video codec not supported by this browser");
          }
          return;
        }

        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            networkErrorRecoveries++;
            if (networkErrorRecoveries <= MAX_NETWORK_RETRIES) {
              onRetryingLatest.current?.(true);
              const delay = Math.min(
                NETWORK_RETRY_BASE_MS * 2 ** (networkErrorRecoveries - 1),
                NETWORK_RETRY_MAX_MS
              );
              networkRetryTimer = setTimeout(() => {
                networkRetryTimer = null;
                if (hlsRef.current !== hls) return;
                hls.startLoad();
              }, delay);
            } else {
              hls.destroy();
              hlsRef.current = null;
              onRetryingLatest.current?.(false);
              onErrorLatest.current?.("Network error loading video. Please check your connection and try again.");
            }
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            mediaErrorRecoveries++;
            if (mediaErrorRecoveries <= 2) {
              hls.recoverMediaError();
            } else {
              hls.destroy();
              hlsRef.current = null;
              onErrorLatest.current?.("Video playback error");
            }
            break;
          default:
            hls.destroy();
            hlsRef.current = null;
            onErrorLatest.current?.("Video failed to load");
            break;
        }
      });

      hls.loadSource(hlsUrl);
      hls.attachMedia(video);
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = hlsUrl;
      video.load();
    } else {
      onErrorLatest.current?.("HLS playback is not supported in this browser");
    }

    return () => {
      if (networkRetryTimer) {
        clearTimeout(networkRetryTimer);
        networkRetryTimer = null;
      }
      onRetryingLatest.current?.(false);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [hlsUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (video && video.readyState >= 1) {
      video.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  return (
    <video
      ref={setVideoRef}
      className={className}
      preload="auto"
      playsInline
      muted={muted}
      onTimeUpdate={(e) => onTimeUpdate?.(e.currentTarget.currentTime)}
      onDurationChange={(e) => onDurationChange?.(e.currentTarget.duration)}
      onPlay={() => onPlay?.()}
      onPause={() => onPause?.()}
      onEnded={() => onEnded?.()}
      onProgress={(e) => onProgress?.(e.currentTarget)}
      onWaiting={() => onWaiting?.()}
      onPlaying={() => onPlaying?.()}
      onLoadedData={(e) => {
        if (playbackSpeed !== 1) {
          e.currentTarget.playbackRate = playbackSpeed;
        }
      }}
      crossOrigin="anonymous"
      data-testid="video-cloudflare"
    />
  );
}
